import { Fragment } from 'react';

import { PageHeader, RoleBadge } from '../../components/ui/index.js';

const ROLES = ['owner', 'admin', 'supervisor', 'operator'] as const;

interface PermRow {
  label: string;
  perms: Record<(typeof ROLES)[number], '✓' | '—' | string>;
}

interface PermGroup {
  title: string;
  rows: PermRow[];
}

const PERMS: PermGroup[] = [
  {
    title: 'Пользователи',
    rows: [
      {
        label: 'Создавать операторов',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
      {
        label: 'Удалять операторов',
        perms: { owner: '✓', admin: '—', supervisor: '—', operator: '—' },
      },
      { label: 'Изменять роли', perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' } },
      {
        label: 'Просматривать команду',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '—' },
      },
    ],
  },
  {
    title: 'Каналы и интеграции',
    rows: [
      {
        label: 'Подключать каналы',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
      {
        label: 'Управлять виджетами',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
      {
        label: 'Управлять ботами',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
      {
        label: 'Настройки коннекторов',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
    ],
  },
  {
    title: 'Тикеты',
    rows: [
      {
        label: 'Видеть все тикеты',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: 'только свои' },
      },
      {
        label: 'Отвечать клиентам',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '✓' },
      },
      {
        label: 'Назначать тикеты',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '✓' },
      },
      {
        label: 'Удалять тикеты',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
      {
        label: 'Объединять тикеты',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '—' },
      },
      { label: 'Эскалировать', perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '✓' } },
      {
        label: 'Массовые действия',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '—' },
      },
    ],
  },
  {
    title: 'Аналитика',
    rows: [
      {
        label: 'Просматривать дашборд',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '—' },
      },
      {
        label: 'Детальные отчёты',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '—' },
      },
      {
        label: 'Экспорт данных',
        perms: { owner: '✓', admin: '✓', supervisor: '✓', operator: '—' },
      },
    ],
  },
  {
    title: 'Безопасность',
    rows: [
      {
        label: 'Audit log (полный)',
        perms: { owner: '✓', admin: '—', supervisor: '—', operator: '—' },
      },
      {
        label: 'Настройки 2FA policy',
        perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' },
      },
      { label: 'IP-whitelist', perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' } },
      { label: 'API-ключи', perms: { owner: '✓', admin: '✓', supervisor: '—', operator: '—' } },
    ],
  },
];

export function SettingsRoles(): JSX.Element {
  return (
    <div className="inbx-page">
      <PageHeader title="Роли и права" />
      <div className="inbx-page__body" style={{ maxWidth: 1000 }}>
        <div className="inbx-card">
          <table className="inbx-table">
            <thead>
              <tr>
                <th>Возможность</th>
                {ROLES.map((r) => (
                  <th key={r} style={{ textAlign: 'center' }}>
                    <RoleBadge role={r} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMS.map((g) => (
                <Fragment key={g.title}>
                  <tr style={{ background: 'var(--inbx-bg-secondary)' }}>
                    <td colSpan={5} style={{ fontWeight: 600, padding: '8px 12px' }}>
                      {g.title}
                    </td>
                  </tr>
                  {g.rows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      {ROLES.map((r) => (
                        <td key={r} style={{ textAlign: 'center' }}>
                          {row.perms[r] === '✓' ? (
                            <i className="ti-check" style={{ color: 'var(--inbx-success)' }} />
                          ) : row.perms[r] === '—' ? (
                            <i className="ti-minus" style={{ color: 'var(--inbx-text-muted)' }} />
                          ) : (
                            <span
                              style={{
                                color: 'var(--inbx-warning-text)',
                                fontSize: 'var(--inbx-fz-xs)',
                              }}
                            >
                              {row.perms[r]}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="inbx-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <i className="ti-lock" style={{ fontSize: 24, color: 'var(--inbx-text-muted)' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>Пользовательские роли</div>
            <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              Доступны на Enterprise плане
            </div>
          </div>
          <a href="/settings/billing" style={{ color: 'var(--inbx-accent)' }}>
            Обновить план →
          </a>
        </div>
      </div>
    </div>
  );
}
